import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { transparentize } from 'polished'
import { space as styledSpace, width } from 'styled-system'
import { DialogContent, DialogOverlay } from '@reach/dialog'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { fontSizes, fontWeights, radius, space } from 'theme'
import { pxToRem } from 'helpers'
import { ERROR_MESSAGE } from 'consts'
import errorHelper from 'utils/errorHelper'
import { Box, Flex } from 'components/atoms/Layout'
import Image from 'components/atoms/Image'
import { H4, Text } from 'components/atoms/Typography'
import Button from 'components/atoms/Button'
import ButtonWithLoading from 'components/molecules/ButtonWithLoading'
import CalendarItemTooltip from 'routes/Calendar/components/CalendarItemTooltip'
import ErrorLabel from 'components/atoms/ErrorLabel'

const StyledDialogOverlay = styled(DialogOverlay)`
  &&& {
    background-color: ${({ theme }) => transparentize(0.2, theme.colors.background_modal_overlay)};
    z-index: 2147483001;
  }
`

const StyledDialogContent = styled(DialogContent)`
  &&& {
    background-color: ${({ theme }) => theme.colors.background_card};
    position: relative;
    max-width: 500px;
    width: 100%;
    padding: 0;
    border-radius: ${radius.l};
    ${styledSpace};
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: 330px;
    top: 40%;
    transform: translate(0, -50%);
    outline: none;
  }
`

const CloseIconWrapper = styled(Box)`
  position: absolute;
  top: -6px;
  right: -9px;
  background: ${({ theme }) => theme.colors.background_card};
  height: 20px;
  width: 20px;
  border-radius: ${radius.pill};
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
`

const StyledDialogEnvironmentWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.background_modal_header};
  ${({ $isTop }) => $isTop && `border-radius: ${radius.l} ${radius.l} 0 0;`}
  ${({ $isBottom }) => $isBottom && `border-radius: 0 0 ${radius.l} ${radius.l};`}
`

const StyledInput = styled.input`
  padding: ${space.m} 100px ${space.m} ${space.s};
  border: 1px solid ${({ isError, theme }) => (isError ? theme.colors.error : theme.colors.border_color)};
  background: transparent;
  border-radius: ${radius.l};
  height: ${pxToRem(40)};
  color: ${({ theme }) => theme.colors.primaryText};
  font-size: ${fontSizes.xs};
  :focus {
    outline-style: none;
  }
  &::-webkit-input-placeholder,
  &:-moz-placeholder,
  &::placeholder {
    color: ${({ theme }) => theme.colors.input_placeholder_color};
    font-size: ${fontSizes.xs};
    font-weight: ${fontWeights.normal};
  }
  ${styledSpace};
  ${width};
`

const StyledDomainEnding = styled(Flex)`
  position: absolute;
  top: 50%;
  transform: translate(0, -50%);
  right: ${space.xs};
  cursor: pointer;
  width: 100px;
`

const { HANDLE } = {
  HANDLE: 'handle',
}

const ConnectingZendeskModal = ({ isOpen, handleDismiss, handleAddProfile, entityId, profileHandle = '' }) => {
  const formRef = useRef(null)
  const tooltipRef = useRef(null)

  const [isConnectingZendesk, setIsConnectingZendesk] = useState(false)

  const handleSubmitForm = async (values) => {
    setIsConnectingZendesk(true)
    try {
      document.location = `/api/connect/zendesk?entityId=${entityId}&subdomain=${values[HANDLE]}`
    } catch (error) {
      errorHelper({ error, componentName: ConnectingZendeskModal.displayName, functionName: 'handleSubmitForm' })
      setIsConnectingZendesk(false)
    }
  }

  const handleSubmitFormFromButton = () => {
    formRef.current.handleSubmit()
  }

  const createValidationSchema = () => {
    return Yup.object().shape({
      [HANDLE]: Yup.string()
        .max(50, 'Zendesk subdomain is too long - should be 50 chars maximum.')
        // .test('test-handle', 'Please enter a valid Zendesk subdomain', (value) => value && value.split('.').length >= 2)
        .required(`Zendesk subdomain is required`),
    })
  }

  const handleClickCloseModal = () => {
    if (!isConnectingZendesk) {
      handleDismiss()
    }
  }

  return (
    <StyledDialogOverlay isOpen={isOpen} onDismiss={() => {}}>
      <Box m="0 auto" width="100%" height="100%" p="l">
        <StyledDialogContent tabIndex="0">
          <StyledDialogEnvironmentWrapper px="m" $isTop>
            <H4 my="m">Authorize Vista Social on Zendesk</H4>
          </StyledDialogEnvironmentWrapper>
          <Flex flexDirection="column" mt="m" px="m" flex="1">
            <Flex flexDirection="column" width="100%">
              <Formik
                ref={formRef}
                initialValues={{
                  [HANDLE]: profileHandle || '',
                }}
                validationSchema={createValidationSchema}
                onSubmit={handleSubmitForm}
              >
                {({ values, touched, errors, setFieldValue }) => (
                  <Flex width="100%" height="100%" flexDirection="column">
                    <Box>
                      <Flex mb="xs" alignItems="center">
                        <Text fontSize="s">Zendesk subdomain</Text>
                      </Flex>
                      <Text color="secondaryText" fontSize="xs" mb="m">
                        Vista Social needs permission to access Zendesk on your behalf to create support tickets. Please
                        enter the Zendesk subdomain you would like to authorize.
                      </Text>

                      <Flex flexDirection="column" position="relative" width="100%">
                        <StyledInput
                          placeholder="Subdomain"
                          type="text"
                          value={values[HANDLE]}
                          onChange={({ target: { value } }) => {
                            const value_updated = value.replace(/[^a-zA-Z0-9\-]/g, '')
                            setFieldValue(HANDLE, value_updated)
                          }}
                          width="100%"
                          error={errors[HANDLE] && touched[HANDLE] && errors[HANDLE]}
                        />
                        <StyledDomainEnding justifyContent="center" alignItems="center">
                          <Text fontWeight="medium">.zendesk.com</Text>
                        </StyledDomainEnding>
                      </Flex>
                      {errors[HANDLE] && touched[HANDLE] && errors[HANDLE] && (
                        <ErrorLabel mt="xxs" textAlign="left" error={errors[HANDLE]} />
                      )}
                    </Box>
                  </Flex>
                )}
              </Formik>
            </Flex>
          </Flex>

          <StyledDialogEnvironmentWrapper p="m" justifyContent="space-between" $isBottom>
            <Button.Gray mr="m" isSmall onClick={handleClickCloseModal} isDisabled={isConnectingZendesk}>
              Cancel
            </Button.Gray>
            <ButtonWithLoading
              isSmall
              onClick={handleSubmitFormFromButton}
              isLoading={isConnectingZendesk}
              type="submit"
            >
              {isConnectingZendesk ? 'Connecting' : 'Connect'}
            </ButtonWithLoading>
          </StyledDialogEnvironmentWrapper>

          <CloseIconWrapper className="modal-close-icon" onClick={handleClickCloseModal}>
            <Image width="10px" height="10px" src="/assets/clear.svg" />
          </CloseIconWrapper>
        </StyledDialogContent>

        <CalendarItemTooltip ref={tooltipRef} />
      </Box>
    </StyledDialogOverlay>
  )
}

ConnectingZendeskModal.defaultProps = {
  profileHandle: '',
}

ConnectingZendeskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleDismiss: PropTypes.func.isRequired,
  handleAddProfile: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
  profileHandle: PropTypes.string,
}

ConnectingZendeskModal.displayName = 'ConnectingZendeskModal'

export default ConnectingZendeskModal
